import { useLayoutEffect, useRef, useState } from "react";
import { useStore } from "../app/store.ts";
import { ViewportMessages } from "../ui/ViewportMessages.tsx";
import {
  usePresenceTransition,
  VIEWPORT_MODE_TRANSITION_MS,
} from "./usePresenceTransition.ts";

const FRAME_TRANSITION_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * Compute the largest frame that fits within `fill` fraction of the
 * container while maintaining the given aspect ratio (w/h).
 */
function fitFrame(
  containerW: number,
  containerH: number,
  ratio: number,
  fill = 0.8,
): { width: number; height: number } {
  const maxW = containerW * fill;
  const maxH = containerH * fill;

  if (maxW / maxH > ratio) {
    // Container is wider than the frame — height-constrained
    return { width: maxH * ratio, height: maxH };
  }
  // Container is taller — width-constrained
  return { width: maxW, height: maxW / ratio };
}

/**
 * Camera frame overlay — shows exactly what will be captured during export.
 *
 * The frame's aspect ratio is derived from the store's `renderWidth` and
 * `renderHeight` (the same values the export pipeline uses), so the on-
 * screen extents correspond 1:1 to the exported image's extents. The
 * frame's CSS size is then fit to ~80% of the viewport container; the
 * literal pixel dimensions never drive the visible frame size.
 *
 * The `data-camera-frame` element is always present and correctly sized
 * (regardless of the `showFrustum` toggle) so `cropToFrame` in
 * `exportUtils.ts` can rely on its bounding rect for pixel-accurate crops.
 */
export function CameraFrameOverlay() {
  const show = useStore((s) => s.showFrustum);
  const renderWidth = useStore((s) => s.renderWidth);
  const renderHeight = useStore((s) => s.renderHeight);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isVisible } = usePresenceTransition(show);

  const ratio = renderWidth / renderHeight;

  const [frameSize, setFrameSize] = useState<{ width: number; height: number }>(
    {
      width: 0,
      height: 0,
    },
  );

  // useLayoutEffect so the first measurement completes before the browser
  // paints — the user never sees an un-measured (zero-sized) frame.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function measure() {
      const rect = container!.getBoundingClientRect();
      setFrameSize(fitFrame(rect.width, rect.height, ratio));
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [ratio]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 3,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        data-camera-frame
        style={{
          width: frameSize.width,
          height: frameSize.height,
          position: "relative",
          borderRadius: 2,
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.35)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            borderRadius: 2,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "scale(1)" : "scale(0.96)",
            transformOrigin: "center center",
            transition:
              `opacity ${VIEWPORT_MODE_TRANSITION_MS}ms ${FRAME_TRANSITION_EASING}, ` +
              `transform ${VIEWPORT_MODE_TRANSITION_MS}ms ${FRAME_TRANSITION_EASING}`,
            willChange: "opacity, transform",
          }}
        />
      </div>

      {/* Viewport status messages — pinned to the top-right of the viewport */}
      <ViewportMessages />
    </div>
  );
}
